import { useState } from 'react'
import { FiX, FiSend } from 'react-icons/fi'
import './BugReportModal.css'

// Bug reports POST to the bug-report Cloudflare Worker (cf-utility-functions),
// which files a Linear issue. No reporter-service dependency.
const DEFAULT_BUG_REPORT_URL = 'https://bug-report.greg-6b0.workers.dev'

type Props = {
  onClose: () => void
  userId?: string | null
  userEmail?: string | null
}

const CATEGORIES = ['Bug', 'Feature Request', 'Other'] as const

function BugReportModal({ onClose, userId, userEmail }: Props) {
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('Bug')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!description.trim()) return
    setSubmitting(true)
    setError(null)

    const payload = {
      description: description.trim(),
      category,
      userId: userId || null,
      userEmail: userEmail || null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
    }

    try {
      const webhookUrl = import.meta.env.VITE_BUG_REPORT_URL || DEFAULT_BUG_REPORT_URL
      const resp = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!resp.ok) throw new Error('Failed to submit')
      setSubmitted(true)
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <>
        <div className="bug-report-overlay" onClick={onClose} />
        <div className="bug-report-modal">
          <button type="button" className="bug-report-close" onClick={onClose} aria-label="Close">
            <FiX size={18} />
          </button>
          <div className="bug-report-success">
            <h2>Thanks for the feedback!</h2>
            <p>We&apos;ll look into this and get back to you if needed.</p>
            <button type="button" className="bug-report-btn primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="bug-report-overlay" onClick={onClose} />
      <div className="bug-report-modal">
        <button type="button" className="bug-report-close" onClick={onClose} aria-label="Close">
          <FiX size={18} />
        </button>
        <h2>Report an Issue</h2>
        <p className="bug-report-subtitle">
          Let us know what&apos;s going on and we&apos;ll get it sorted.
        </p>

        <div className="bug-report-field">
          <label htmlFor="bug-category">Category</label>
          <select
            id="bug-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as (typeof CATEGORIES)[number])}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="bug-report-field">
          <label htmlFor="bug-description">What happened?</label>
          <textarea
            id="bug-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue or suggestion..."
            rows={5}
            autoFocus
          />
        </div>

        {error && <p className="bug-report-error">{error}</p>}

        <div className="bug-report-actions">
          <button type="button" className="bug-report-btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bug-report-btn primary"
            onClick={handleSubmit}
            disabled={!description.trim() || submitting}
          >
            <FiSend size={14} />
            {submitting ? 'Sending...' : 'Submit'}
          </button>
        </div>
      </div>
    </>
  )
}

export default BugReportModal
